import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { TrendingUp, TrendingDown, ExternalLink, Loader2 } from 'lucide-react';
import { getStockFast } from '../../utils/api';
import { SignalBadge } from '../common/Badge';

interface TickerLinkProps {
  ticker: string;
  children?: React.ReactNode;
}

interface StockPreview {
  ticker: string;
  name?: string;
  current_price?: number;
  price_change_pct?: number;
  sentiment_score?: number;
  disconnect_score?: number;
  signal?: string;
}

export function TickerLink({ ticker, children }: TickerLinkProps) {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState<StockPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linkRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = async () => {
    // Delay showing preview
    timeoutRef.current = setTimeout(async () => {
      setShowPreview(true);
      if (!preview && !loading) {
        setLoading(true);
        try {
          const data = await getStockFast(ticker);
          setPreview({
            ticker: data.ticker,
            name: data.name,
            current_price: data.technical?.current_price,
            sentiment_score: data.sentiment?.sentiment_score,
            disconnect_score: data.disconnect?.disconnect_score,
            signal: data.disconnect?.signal,
          });
        } catch (e) {
          // Failed to load preview, just show link
        } finally {
          setLoading(false);
        }
      }
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowPreview(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/analysis?ticker=${ticker}`);
  };

  return (
    <span className="relative inline-block" ref={linkRef}>
      <span
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={clsx(
          'cursor-pointer font-semibold px-1.5 py-0.5 rounded',
          'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20',
          'transition-colors inline-flex items-center gap-1'
        )}
      >
        {children || ticker}
        <ExternalLink className="h-3 w-3 opacity-50" />
      </span>

      {/* Preview Card */}
      {showPreview && (
        <div
          className={clsx(
            'absolute z-50 left-0 top-full mt-2',
            'w-64 p-4 rounded-xl shadow-xl',
            'bg-[var(--bg-card)] border border-[var(--border-color)]',
            'animate-in fade-in-0 zoom-in-95 duration-200'
          )}
          onMouseEnter={() => setShowPreview(true)}
          onMouseLeave={handleMouseLeave}
        >
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--text-muted)]" />
            </div>
          ) : preview ? (
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-[var(--text-primary)]">{preview.ticker}</div>
                  {preview.name && (
                    <div className="text-xs text-[var(--text-muted)] truncate max-w-[150px]">
                      {preview.name}
                    </div>
                  )}
                </div>
                {preview.signal && <SignalBadge signal={preview.signal} />}
              </div>

              {/* Price */}
              {preview.current_price && (
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-[var(--text-primary)]">
                    ${preview.current_price.toFixed(2)}
                  </span>
                  {preview.price_change_pct !== undefined && (
                    <span
                      className={clsx(
                        'flex items-center gap-1 text-sm font-medium',
                        preview.price_change_pct >= 0 ? 'text-emerald-500' : 'text-red-500'
                      )}
                    >
                      {preview.price_change_pct >= 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {preview.price_change_pct >= 0 ? '+' : ''}
                      {preview.price_change_pct.toFixed(2)}%
                    </span>
                  )}
                </div>
              )}

              {/* Scores */}
              <div className="grid grid-cols-2 gap-2">
                {preview.sentiment_score !== undefined && (
                  <div className="p-2 rounded-lg bg-[var(--bg-secondary)]">
                    <div className="text-xs text-[var(--text-muted)]">Sentiment</div>
                    <div
                      className={clsx(
                        'font-bold',
                        preview.sentiment_score >= 60
                          ? 'text-emerald-500'
                          : preview.sentiment_score <= 40
                          ? 'text-red-500'
                          : 'text-amber-500'
                      )}
                    >
                      {preview.sentiment_score.toFixed(0)}
                    </div>
                  </div>
                )}
                {preview.disconnect_score !== undefined && (
                  <div className="p-2 rounded-lg bg-[var(--bg-secondary)]">
                    <div className="text-xs text-[var(--text-muted)]">Disconnect</div>
                    <div
                      className={clsx(
                        'font-bold',
                        preview.disconnect_score >= 60
                          ? 'text-emerald-500'
                          : preview.disconnect_score <= 40
                          ? 'text-red-500'
                          : 'text-amber-500'
                      )}
                    >
                      {preview.disconnect_score.toFixed(0)}
                    </div>
                  </div>
                )}
              </div>

              {/* CTA */}
              <button
                onClick={handleClick}
                className="w-full py-2 text-sm font-medium text-center rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors cursor-pointer"
              >
                View Full Analysis
              </button>
            </div>
          ) : (
            <div className="text-center py-2 text-[var(--text-muted)] text-sm">
              Click to view {ticker} analysis
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// Common stock tickers to recognize
const COMMON_TICKERS = new Set([
  'AAPL', 'MSFT', 'GOOGL', 'GOOG', 'AMZN', 'META', 'NVDA', 'TSLA', 'AMD', 'INTC',
  'NFLX', 'PYPL', 'ADBE', 'CRM', 'ORCL', 'CSCO', 'AVGO', 'TXN', 'QCOM', 'IBM',
  'JPM', 'BAC', 'WFC', 'GS', 'MS', 'C', 'V', 'MA', 'AXP', 'BLK',
  'JNJ', 'UNH', 'PFE', 'MRK', 'ABBV', 'LLY', 'TMO', 'ABT', 'DHR', 'BMY',
  'XOM', 'CVX', 'COP', 'SLB', 'OXY', 'EOG', 'MPC', 'PSX', 'VLO', 'PXD',
  'DIS', 'CMCSA', 'VZ', 'T', 'TMUS', 'CHTR', 'NWSA', 'PARA', 'WBD', 'FOX',
  'WMT', 'COST', 'HD', 'TGT', 'LOW', 'AMGN', 'GILD', 'BIIB', 'REGN', 'VRTX',
  'BA', 'CAT', 'GE', 'HON', 'UNP', 'UPS', 'RTX', 'LMT', 'NOC', 'GD',
  'KO', 'PEP', 'MCD', 'SBUX', 'NKE', 'PG', 'CL', 'EL', 'KMB', 'GIS',
  'SPY', 'QQQ', 'IWM', 'DIA', 'VTI', 'VOO', 'ARKK', 'XLF', 'XLK', 'XLE',
]);

/**
 * Process text to make ticker symbols clickable
 */
export function processTickerLinks(text: string): (string | React.ReactElement)[] {
  // Match potential tickers: 1-5 uppercase letters, not preceded/followed by lowercase
  const tickerRegex = /\b([A-Z]{1,5})\b/g;
  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = tickerRegex.exec(text)) !== null) {
    const ticker = match[1];

    // Only link if it's a known ticker (to avoid false positives like "AI", "I", "A")
    if (COMMON_TICKERS.has(ticker)) {
      // Add text before the ticker
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // Add ticker link
      parts.push(<TickerLink key={`${ticker}-${match.index}`} ticker={ticker} />);
      lastIndex = match.index + match[0].length;
    }
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
